// src/services/authService.ts
import axios from 'axios';
import localStorageService from './localStorageService';
import { 
    PERIOD_CONFIG, 
    USER_LOGIN, 
    AUTH_REFRESH,
    USER_CREATE, 
    COLLEGES_LIST, 
    TEAMS_LIST, 
    TEAMS_SEARCH,
    PASSWORD_RESET_REQUEST,
    PASSWORD_RESET,
    ADMIN_ALL_USERS,
    ADMIN_GET_SPORTS,
    ADMIN_GET_ALL_TEAMS,
    ADMIN_GET_TEAMS,
    ADMIN_GET_TEAM_SEAT_INFO,
    ADMIN_GET_TEAM_DETAILS,
    ADMIN_ONBOARD_TEAM,
    ADMIN_GET_USER,
    ADMIN_CREATE_USER,
    ADMIN_TEAM_ADMINS,
    ADMIN_UPDATE_USER,
    ADMIN_SEARCH_USERS,
    ADMIN_BILLING_PREVIEW,
    ADMIN_GENERATE_INVOICE,
    ADMIN_CACHE_QUERIES,
    ADMIN_TEAM_REQUESTS,
    ADMIN_RECORDBOOK_REQUESTS,
    ADMIN_USER_SEAT_REQUESTS,
    UPLOAD_NOTIFICATIONS_API,
    VALIDATE_RESET_TOKEN,
    REGENERATE_RESET_TOKEN,
    ANALYTICS,
    TRIAL_END_API,
    TEAM_SUBSCRIPTION_ID_API,
    STRIPE_CREATE_SESSION,
    STRIPE_GENERATE_INVOICE
} from '../constants/routes';

class AuthService {
    public AuthenticateUser = async (
        payload: any
    ) => {
        try {
            const response = await axios.post(PERIOD_CONFIG, payload, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public UserLogin = async (payload: any) => {
        try {
            const response = await axios.post(USER_LOGIN, payload, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public RefreshAuth = async (refreshToken: string) => {
        try {
            const response = await axios.post(
                AUTH_REFRESH,
                { refresh_token: refreshToken },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public UserCreate = async (payload: any) => { 
        try {
            const response = await axios.post(USER_CREATE, payload, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }


    public getSearchAnalytics = async () => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(ANALYTICS, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getAnalyticsConfig = async () => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            
            const configUrl = ANALYTICS.replace('searches', 'config'); 

            const response = await axios.get(configUrl, { 
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            console.error("Error fetching analytics config:", error);
            return error.response || error;
        }
    }

    public getTrialEnd = async () => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;

            const response = await axios.get(TRIAL_END_API, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getTeamSubscriptionId = async (sportCode: string) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;

            const response = await axios.get(`${TEAM_SUBSCRIPTION_ID_API}?sport_code=${encodeURIComponent(sportCode)}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public createStripeSession = async (payload: {
        team_subscription_ids: string[];
        billing_user_id: number;
        amount: number;
        currency: string;
        currency_code?: string;
    }) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;

            const response = await axios.post(STRIPE_CREATE_SESSION, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public generateInvoice = async (payload: any) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;

            const response = await axios.post(STRIPE_GENERATE_INVOICE, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getColleges = async () => {
        try {
            const response = await axios.get(COLLEGES_LIST);
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getTeams = async () => {
        try {
            const response = await axios.get(TEAMS_LIST);
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public searchTeams = async (q: string, limit = 20, signal?: AbortSignal) => {
        try {
            const response = await axios.get(TEAMS_SEARCH, {
                params: { q, limit },
                signal,
            });
            return response;
        } catch (error: any) {
            if (axios.isCancel(error)) {
                throw error;
            }
            return error.response || error;
        }
    }

    public requestPasswordReset = async (usernameOrEmail: string) => {
        try {
            const response = await axios.post(`${PASSWORD_RESET_REQUEST}?usernameorEmail=${encodeURIComponent(usernameOrEmail)}`, {}, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public resetPassword = async (token: string, password: string) => {
        try {
            const response = await axios.post(`${PASSWORD_RESET}?token=${encodeURIComponent(token)}&password=${encodeURIComponent(password)}`, {}, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public validateResetToken = async (token: string) => {
        try {
            const response = await axios.get(
                `${VALIDATE_RESET_TOKEN}?token=${encodeURIComponent(token)}`
            );
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    };

    public regenerateResetToken = async (token: string) => {
        try {
            const response = await axios.post(
                `${REGENERATE_RESET_TOKEN}?token=${encodeURIComponent(token)}`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    };

    // --- ADMIN METHODS ---

    public getAllUsers = async (period = 'all') => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(`${ADMIN_ALL_USERS}?period=${period}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getSports = async () => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(ADMIN_GET_SPORTS, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getAllTeams = async () => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(ADMIN_GET_ALL_TEAMS, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getAdminTeams = async () => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(ADMIN_GET_TEAMS, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getTeamSeatInfo = async (teamId: number, signal?: AbortSignal) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(`${ADMIN_GET_TEAM_SEAT_INFO}?team_id=${teamId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                signal,
            });
            return response;
        } catch (error: any) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'AbortError' || error?.name === 'CanceledError') {
                throw error;
            }
            return error.response || error;
        }
    }
    
    public getTeamDetails = async (teamCode: number | string) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(`${ADMIN_GET_TEAM_DETAILS}?team_code=${teamCode}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public onboardTeam = async (teamData: any) => {
        try {
            // Ensure team_code is a number
            const processedTeamData = {
                ...teamData,
                team_code: Number(teamData.team_code)
            };
            
            console.log('Team data being sent to API:', processedTeamData);
            
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.post(ADMIN_ONBOARD_TEAM, processedTeamData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            console.error('Error onboarding team:', error);
            if (error.response) {
                console.error('Error response:', error.response.data);
                console.error('Status:', error.response.status);
            }
           throw error;
        }
    }

    public getUserById = async (userId: number) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(`${ADMIN_GET_USER}/${userId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public adminCreateUser = async (userData: any) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.post(ADMIN_CREATE_USER, userData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getTeamAdminsSummary = async () => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(ADMIN_TEAM_ADMINS, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public createTeamAdmin = async (teamId: number) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.post(ADMIN_TEAM_ADMINS, { team_id: teamId }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public updateUser = async (userId: number, userData: any) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            
            // Convert field names to match backend expectations
            const payload = {
                UserName: userData.username,
                Email: userData.email,
                fullName: userData.full_name,
                TeamId: userData.team_id,
                designation: userData.designation
            };
            
            // Only include password if it was provided
            if (userData.password) {
                Object.assign(payload, { password: userData.password });
            }
            
            const response = await axios.put(`${ADMIN_UPDATE_USER}/${userId}`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            console.error('Error updating user:', error);
            return error.response || error;
        }
    }
    
    public searchUsers = async (query: string, limit: number = 10, offset: number = 0) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(`${ADMIN_SEARCH_USERS}?query=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getBillingPreview = async (userId: number, signal?: AbortSignal) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(`${ADMIN_BILLING_PREVIEW}/${userId}/billing-preview`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                signal,
            });
            return response;
        } catch (error: any) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'AbortError' || error?.name === 'CanceledError') {
                throw error;
            }
            return error.response || error;
        }
    }

    public generateAdminInvoice = async (payload: { user_id: number; sport_codes: string[] }) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.post(ADMIN_GENERATE_INVOICE, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getCacheQueries = async (params: {
        usernames?: string[];
        team_id?: number;
        sport_code?: string;
        entity?: string;
        query?: string;
        days?: number;
        limit?: number;
        offset?: number;
    }) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const searchParams = new URLSearchParams();

            (params.usernames || []).forEach((username) => {
                if (username?.trim()) {
                    searchParams.append('usernames', username.trim());
                }
            });

            if (params.team_id != null) searchParams.set('team_id', String(params.team_id));
            if (params.sport_code?.trim()) searchParams.set('sport_code', params.sport_code.trim());
            if (params.entity?.trim()) searchParams.set('entity', params.entity.trim());
            if (params.query?.trim()) searchParams.set('query', params.query.trim());
            if (params.days != null && params.days >= 1) searchParams.set('days', String(params.days));
            if (params.limit != null) searchParams.set('limit', String(params.limit));
            if (params.offset != null) searchParams.set('offset', String(params.offset));

            const response = await axios.get(
                `${ADMIN_CACHE_QUERIES}?${searchParams.toString()}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                }
            );
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public deleteCacheQueries = async (ids: number[]) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.delete(ADMIN_CACHE_QUERIES, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                data: { ids },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getTeamRequests = async () => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(ADMIN_TEAM_REQUESTS, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public approveTeamRequest = async (requestId: number) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.patch(`${ADMIN_TEAM_REQUESTS}/${requestId}`, { status: 'completed' }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public rejectTeamRequest = async (requestId: number) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.patch(`${ADMIN_TEAM_REQUESTS}/${requestId}`, { status: 'rejected' }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getRecordbookRequests = async (status: string = 'requested') => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(`${ADMIN_RECORDBOOK_REQUESTS}?status=${encodeURIComponent(status)}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public markRecordbookRequestUploaded = async (requestId: number) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.post(`${ADMIN_RECORDBOOK_REQUESTS}/${requestId}/uploaded`, {}, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public getAdminUserRequests = async (status?: string, teamId?: number) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;

            const params = new URLSearchParams();
            if (status) params.set('status', status);
            if (Number.isFinite(teamId as any)) params.set('team_id', String(teamId));

            const query = params.toString();
            const url = query ? `${ADMIN_USER_SEAT_REQUESTS}?${query}` : ADMIN_USER_SEAT_REQUESTS;
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public approveAdminUserRequest = async (requestId: number, adminNote?: string) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const payload: any = {};
            if (adminNote && adminNote.trim()) payload.admin_note = adminNote.trim();

            const response = await axios.post(`${ADMIN_USER_SEAT_REQUESTS}/${requestId}/approve`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public rejectAdminUserRequest = async (requestId: number, adminNote?: string) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const payload: any = {};
            if (adminNote && adminNote.trim()) payload.admin_note = adminNote.trim();

            const response = await axios.post(`${ADMIN_USER_SEAT_REQUESTS}/${requestId}/reject`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    // Upload Notifications
    public getUploadNotifications = async () => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.get(UPLOAD_NOTIFICATIONS_API, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }

    public markNotificationSeen = async (requestId: number) => {
        try {
            const userInfo = localStorageService.getUserInfo();
            const token = userInfo.access_token;
            const response = await axios.post(`${UPLOAD_NOTIFICATIONS_API}/${requestId}/mark-seen`, {}, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            return response;
        } catch (error: any) {
            return error.response || error;
        }
    }
}

export default AuthService;
